import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { plainToInstance } from "class-transformer";

import { LoggerService } from "@core/services/logger.service";
import { environment } from "@env/environment";
import { buildQueryParams, PaginatedResponse, QueryParams } from "@shared/models/query-params.model";
import { Constructor } from "@shared/types/common-types.model";

import { ToastService } from "./toast.service";

type Options = {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: "body";
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
  transferCache?:
    | {
        includeHeaders?: string[];
      }
    | boolean;
};

@Injectable({
  providedIn: "root"
})
export class BaseHttpService {
  protected apiBaseUrl = environment.apiBaseUrl;
  protected readonly http = inject(HttpClient);
  protected readonly logger = inject(LoggerService);
  protected baseEndpoint = "";
  private readonly _toastService = inject(ToastService);

  /**
   * Generic GET method for single items.
   * Automatically transforms response using the type parameter T.
   */
  protected get<T>(params: {
    skipBaseUrl?: boolean;
    endpoint: string;
    options?: Options;
    type?: Constructor<T>;
    showToast?: boolean;
  }): Observable<T> {
    return this.http.get(this.getFullUrl(params.endpoint, params.skipBaseUrl), params.options).pipe(
      map((response) => {
        if (params.type) {
          return plainToInstance(params.type, response, { excludeExtraneousValues: true }) as T;
        }
        return response as T;
      }),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  protected post<TReturn>(params: {
    skipBaseUrl?: boolean;
    endpoint?: string;
    body?: any;
    options?: Options;
    type?: Constructor<TReturn>;
    showToast?: boolean;
  }): Observable<TReturn> {
    return this.http.post(this.getFullUrl(params.endpoint, params.skipBaseUrl), params.body, params.options).pipe(
      map((response) => {
        if (params.type) {
          return plainToInstance(params.type, response, { excludeExtraneousValues: true }) as TReturn;
        }
        return response as TReturn;
      }),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  protected patch<TReturn>(params: {
    skipBaseUrl?: boolean;
    endpoint?: string;
    body?: any;
    options?: Options;
    type?: Constructor<TReturn>;
    showToast?: boolean;
  }): Observable<TReturn> {
    return this.http.patch(this.getFullUrl(params.endpoint, params.skipBaseUrl), params.body, params.options).pipe(
      map((response) => {
        if (params.type) {
          return plainToInstance(params.type, response, { excludeExtraneousValues: true }) as TReturn;
        }
        return response as TReturn;
      }),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  protected put<TReturn>(params: {
    skipBaseUrl?: boolean;
    endpoint?: string;
    body?: any;
    options?: Options;
    type?: Constructor<TReturn>;
    showToast?: boolean;
  }): Observable<TReturn> {
    return this.http.put(this.getFullUrl(params.endpoint, params.skipBaseUrl), params.body, params.options).pipe(
      map((response) => {
        if (params.type) {
          return plainToInstance(params.type, response, { excludeExtraneousValues: true }) as TReturn;
        }
        return response as TReturn;
      }),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  protected delete<TReturn>(params: {
    skipBaseUrl?: boolean;
    endpoint?: string;
    options?: Options;
    type?: Constructor<TReturn>;
    showToast?: boolean;
  }): Observable<TReturn> {
    return this.http.delete(this.getFullUrl(params.endpoint, params.skipBaseUrl), params.options).pipe(
      map((response) => {
        if (params.type) {
          return plainToInstance(params.type, response, { excludeExtraneousValues: true }) as TReturn;
        }
        return response as TReturn;
      }),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  /**
   * Generic GET method for paginated results.
   * Automatically transforms each item using the type parameter T and provides pagination metadata.
   */
  protected getMany<T>(params: {
    skipBaseUrl?: boolean;
    endpoint?: string;
    queryParams?: QueryParams;
    options?: Options;
    type?: Constructor<T>;
    showToast?: boolean;
  }): Observable<PaginatedResponse<T>> {
    const options: Options = params.options || {};

    // Process the query parameters
    const processedParams = buildQueryParams(params.queryParams || {});

    // Set the HTTP parameters
    options.params = {
      ...options.params,
      ...processedParams
    };

    return this.http.get<PaginatedResponse<T>>(this.getFullUrl(params.endpoint, params.skipBaseUrl), options).pipe(
      map((response) => {
        if (params.type && Array.isArray(response.data)) {
          return {
            data: response.data.map((item) => plainToInstance(params.type!, item, { excludeExtraneousValues: true })) as T[],
            total: response.total
          };
        }
        return response as PaginatedResponse<T>;
      }),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  protected getManyWithoutPagination<T>(params: {
    skipBaseUrl?: boolean;
    endpoint?: string;
    queryParams?: QueryParams;
    options?: Options;
    type?: Constructor<T>;
    showToast?: boolean;
  }): Observable<T[]> {
    return this.http.get<T[]>(this.getFullUrl(params.endpoint, params.skipBaseUrl), params.options).pipe(
      map((response) => response.map((item) => plainToInstance(params.type!, item, { excludeExtraneousValues: true })) as T[]),
      catchError((error) => this.handleError(error, params.showToast))
    );
  }

  private getFullUrl(endpoint?: string, skipBaseUrl?: boolean): string {
    if (skipBaseUrl) {
      return `${this.apiBaseUrl}/${endpoint}`;
    } else {
      const baseUrl = this.baseEndpoint ? `${this.apiBaseUrl}/${this.baseEndpoint}` : this.apiBaseUrl;
      if (endpoint) {
        return `${baseUrl}/${endpoint}`;
      }

      return baseUrl;
    }
  }

  private handleError(error: any, showToast: boolean = true): Observable<never> {
    let errorMessage = "An error occurred";

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      if (error.status === 0) {
        errorMessage = "Unable to connect to the server";
      } else if (error.status === HttpStatusCode.Unauthorized) {
        return throwError(() => error);
      } else {
        errorMessage = error.error?.message || error.message || errorMessage;
      }
    }

    if (showToast) {
      this._toastService.error(errorMessage);
    }
    return throwError(() => error);
  }
}
